import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/popover';
import { Textarea } from '@art-of-hospitality-solutions-llc/ui-kit/components/textarea';
import {
  toastError,
  toastSuccess,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/toast';
import ShowFor from '@Components/ShowFor';
import { useUpdateInviteCode } from '@Hooks/useUpdateInviteCode';
import type { RegistrationCode } from '@Types/registrationCode';
import { Pencil } from 'lucide-react';
import { useState, type MouseEvent } from 'react';

const toLines = (text: string) =>
  text
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

/**
 * The invite code list's promo details cell. Staff with `REGISTRATION_CODES_WRITE` can edit the
 * lines in place; each non-empty line is one promo detail. The cell sits inside a clickable row,
 * so its own clicks never reach the row.
 */
export default function PromoDetailsCell({
  inviteCode,
}: {
  inviteCode: RegistrationCode;
}) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const updateMutation = useUpdateInviteCode();
  const promoDetails = inviteCode.promoDetails ?? [];

  const stopRowClick = (event: MouseEvent) => event.stopPropagation();

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) setDraft(promoDetails.join('\n'));
    setOpen(nextOpen);
  };

  const handleSave = async () => {
    try {
      await updateMutation.mutateAsync({
        id: inviteCode.id,
        payload: { promoDetails: toLines(draft) },
      });
      toastSuccess(`Promo details for "${inviteCode.code}" have been updated.`);
      setOpen(false);
    } catch (error) {
      toastError(
        error instanceof Error ? error.message : 'Failed to update promo details',
      );
    }
  };

  return (
    <div className="flex items-start gap-1">
      <div className="min-w-0 flex-1">
        {promoDetails.length ? (
          promoDetails.map((detail, index) => <div key={index}>{detail}</div>)
        ) : (
          <span className="text-zinc-400">-</span>
        )}
      </div>
      <ShowFor permissions={['REGISTRATION_CODES_WRITE']}>
        <Popover open={open} onOpenChange={handleOpenChange}>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 shrink-0 p-0 text-zinc-400 hover:text-zinc-700"
              aria-label={`Edit promo details for ${inviteCode.code}`}
              onClick={stopRowClick}
            >
              <Pencil className="h-3.5 w-3.5" />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            className="w-96 space-y-3"
            onClick={stopRowClick}
          >
            <div className="text-sm font-bold text-zinc-500">
              Promo details · one per line
            </div>
            <Textarea
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              rows={4}
              autoFocus
              placeholder="e.g. Annual fee waived"
            />
            <div className="flex justify-end gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setOpen(false)}
                disabled={updateMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={handleSave}
                loading={updateMutation.isPending}
              >
                Save
              </Button>
            </div>
          </PopoverContent>
        </Popover>
      </ShowFor>
    </div>
  );
}
